'use client';

import { useState, useEffect } from 'react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';

interface AssessmentOption {
  id: string;
  label: string;
  value: string;
}

interface TransformationAssessmentProps {
  className?: string;
}

const TransformationAssessment: React.FC<TransformationAssessmentProps> = ({ className = '' }) => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [selectedIndustry, setSelectedIndustry] = useState('');
  const [selectedChallenge, setSelectedChallenge] = useState('');
  const [showResults, setShowResults] = useState(false);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const industries: AssessmentOption[] = [
    { id: 'telecom', label: 'Telecommunications', value: 'telecom' },
    { id: 'banking', label: 'Banking & Finance', value: 'banking' },
    { id: 'retail', label: 'Retail & E-commerce', value: 'retail' },
  ];

  const challenges: AssessmentOption[] = [
    { id: 'cx', label: 'Customer Experience', value: 'cx' },
    { id: 'ops', label: 'Operational Efficiency', value: 'ops' },
    { id: 'digital', label: 'Digital Transformation', value: 'digital' },
    { id: 'compliance', label: 'Compliance & Security', value: 'compliance' },
  ];

  const handleStartAssessment = () => {
    if (!isHydrated) return;
    if (selectedIndustry && selectedChallenge) {
      setShowResults(true);
    }
  };

  const handleReset = () => {
    if (!isHydrated) return;
    setSelectedIndustry('');
    setSelectedChallenge('');
    setShowResults(false);
  };

  if (!isHydrated) {
    return (
      <section className={`py-20 bg-gradient-to-br from-primary/5 to-accent/5 ${className}`}>
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto bg-card rounded-2xl shadow-xl p-8 lg:p-12 border border-border">
            <div className="text-center">
              <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto"></div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className={`py-20 bg-gradient-to-br from-primary/5 to-accent/5 ${className}`}>
      <div className="container mx-auto px-4">
        <div className="max-w-4xl mx-auto bg-card rounded-2xl shadow-xl p-8 lg:p-12 border border-border">
          {!showResults ? (
            <>
              {/* Header */}
              <div className="text-center mb-12">
                <div className="inline-flex items-center space-x-2 bg-conversion/10 rounded-full px-4 py-2 mb-6">
                  <Icon name="ChartBarIcon" size={20} variant="solid" className="text-conversion" />
                  <span className="font-body text-sm font-medium text-conversion">Free Assessment</span>
                </div>
                <h2 className="font-display font-bold text-3xl lg:text-4xl text-foreground mb-4">
                  Discover Your Transformation Potential
                </h2>
                <p className="font-body text-lg text-muted-foreground">
                  Get personalized insights in 2 minutes
                </p>
              </div>

              {/* Assessment Form */}
              <div className="space-y-8">
                {/* Industry Selection */}
                <div className="space-y-4">
                  <label className="font-display font-semibold text-lg text-foreground block">
                    1. Select Your Industry
                  </label>
                  <div className="grid md:grid-cols-3 gap-4">
                    {industries.map((industry) => (
                      <button
                        key={industry.id}
                        onClick={() => setSelectedIndustry(industry.value)}
                        className={`p-6 rounded-xl border-2 transition-all duration-300 text-left ${
                          selectedIndustry === industry.value
                            ? 'border-primary bg-primary/5 shadow-md'
                            : 'border-border hover:border-primary/50 hover:bg-surface'
                        }`}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <span className="font-body font-medium text-base text-foreground">
                            {industry.label}
                          </span>
                          {selectedIndustry === industry.value && (
                            <Icon name="CheckCircleIcon" size={24} variant="solid" className="text-primary" />
                          )}
                        </div>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Challenge Selection */}
                <div className="space-y-4">
                  <label className="font-display font-semibold text-lg text-foreground block">
                    2. Primary Challenge
                  </label>
                  <div className="grid md:grid-cols-2 gap-4">
                    {challenges.map((challenge) => (
                      <button
                        key={challenge.id}
                        onClick={() => setSelectedChallenge(challenge.value)}
                        className={`p-6 rounded-xl border-2 transition-all duration-300 text-left ${
                          selectedChallenge === challenge.value
                            ? 'border-primary bg-primary/5 shadow-md'
                            : 'border-border hover:border-primary/50 hover:bg-surface'
                        }`}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <span className="font-body font-medium text-base text-foreground">
                            {challenge.label}
                          </span>
                          {selectedChallenge === challenge.value && (
                            <Icon name="CheckCircleIcon" size={24} variant="solid" className="text-primary" />
                          )}
                        </div>
                      </button>
                    ))}
                  </div>
                </div>

                {/* CTA Button */}
                <div className="pt-6">
                  <button
                    onClick={handleStartAssessment}
                    disabled={!selectedIndustry || !selectedChallenge}
                    className={`w-full py-4 rounded-lg font-cta text-lg font-semibold transition-all duration-300 ${
                      selectedIndustry && selectedChallenge
                        ? 'bg-conversion text-conversion-foreground hover:shadow-cta transform hover:scale-105'
                        : 'bg-muted text-muted-foreground cursor-not-allowed'
                    }`}
                  >
                    Get Your Assessment
                  </button>
                </div>
              </div>
            </>
          ) : (
            <>
              {/* Results */}
              <div className="text-center mb-8">
                <div className="inline-flex items-center justify-center w-20 h-20 bg-success/10 rounded-full mb-6">
                  <Icon name="CheckCircleIcon" size={48} variant="solid" className="text-success" />
                </div>
                <h2 className="font-display font-bold text-3xl lg:text-4xl text-foreground mb-4">
                  Your Transformation Roadmap
                </h2>
                <p className="font-body text-lg text-muted-foreground">
                  Based on your {selectedIndustry} industry and {selectedChallenge} focus
                </p>
              </div>

              {/* Recommendations */}
              <div className="space-y-6 mb-8">
                <div className="bg-gradient-to-br from-primary/5 to-accent/5 rounded-xl p-6 border border-primary/20">
                  <div className="flex items-start space-x-4">
                    <div className="bg-primary rounded-lg p-3 flex-shrink-0">
                      <Icon name="LightBulbIcon" size={24} variant="solid" className="text-white" />
                    </div>
                    <div className="space-y-2">
                      <h3 className="font-display font-bold text-xl text-foreground">
                        Recommended Solution: KnowMax + PhygitalMax
                      </h3>
                      <p className="font-body text-sm text-muted-foreground leading-relaxed">
                        Perfect combination for enhancing customer experience in {selectedIndustry} with unified knowledge management and omnichannel engagement.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="grid md:grid-cols-3 gap-4">
                  <div className="bg-card rounded-xl p-6 border border-border text-center">
                    <div className="font-display font-bold text-3xl text-success mb-2">87%</div>
                    <div className="font-body text-sm text-muted-foreground">Expected Satisfaction Increase</div>
                  </div>
                  <div className="bg-card rounded-xl p-6 border border-border text-center">
                    <div className="font-display font-bold text-3xl text-conversion mb-2">$2.1M</div>
                    <div className="font-body text-sm text-muted-foreground">Projected Annual Savings</div>
                  </div>
                  <div className="bg-card rounded-xl p-6 border border-border text-center">
                    <div className="font-display font-bold text-3xl text-accent mb-2">6-8</div>
                    <div className="font-body text-sm text-muted-foreground">Months to Full ROI</div>
                  </div>
                </div>
              </div>

              {/* CTAs */}
              <div className="flex flex-col sm:flex-row gap-4">
                <Link
                  href="/demo"
                  className="flex-1 inline-flex items-center justify-center space-x-2 px-6 py-4 bg-conversion text-conversion-foreground rounded-lg font-cta text-base font-semibold hover:shadow-cta transition-all duration-300"
                >
                  <span>Schedule Demo</span>
                  <Icon name="CalendarIcon" size={20} variant="solid" />
                </Link>
                <button
                  onClick={handleReset}
                  className="flex-1 inline-flex items-center justify-center space-x-2 px-6 py-4 bg-surface text-foreground rounded-lg font-cta text-base font-semibold hover:bg-muted transition-all duration-300"
                >
                  <span>Start Over</span>
                  <Icon name="ArrowPathIcon" size={20} variant="solid" />
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </section>
  );
};

export default TransformationAssessment;